<!--
  THE RANKED-ROW NUMERAL.

  WHY THIS IS NOT `-webkit-text-stroke`. That is what it was, and it is what made
  2, 6 and 9 look wrong. `text-stroke` centres the stroke ON the glyph's outline,
  so half of its width is painted INSIDE the letterform — and on a digit with a
  tight counter (the curl of a 2, the bowls of 6 and 9) the inner half closes the
  counter up and leaves a lump where a hole should be. The heavier the stroke the
  worse it gets, and the stroke has to be heavy to survive a television.

  SVG text with `paint-order: stroke` fixes it properly: the stroke is painted
  FIRST and the fill goes over the top, so the visible band is the outer half of
  the stroke only and every counter stays open at any weight.

  WHY THE WIDTH IS MEASURED. A two-digit rank is nearly twice as wide as a
  one-digit one, and the row used to give every numeral the same column — so
  "10" shouldered its own poster sideways and the last tile in the row was
  clipped. The viewBox is sized per digit-count and the element declares its own
  width — and it also declares that width as `--rank-w`, because the LAYOUT
  cost of a numeral is fixed (`--rank-peek`, the sliver left showing beside the
  poster) whatever the digit count: the tile's CSS needs the real width to hand
  the excess back as negative margin, and a CSS attribute cannot read an SVG
  one any other way. "10" is simply hidden deeper behind its poster than "9",
  and slides out when the tile takes focus.
-->
<script lang="ts">
  interface Props {
    /** 1-based rank. */
    value: number;
    /** Cap height in px. The stroke and the box scale from it. */
    size?: number;
    /** Brighter when its tile has focus. */
    lit?: boolean;
  }

  let { value, size = 96, lit = false }: Props = $props();

  const text = $derived(String(value));
  const digits = $derived(text.length);

  /*
   * A digit of this face is about 0.56 of its cap height wide, and they are set
   * tight. These two numbers are the whole geometry: everything else is derived
   * so a 74px numeral on a phone and a 128px one on a television are the same
   * drawing at two sizes rather than two drawings.
   */
  const W = $derived(Math.round(size * (0.55 * digits + 0.06)));
  const H = $derived(Math.round(size * 1.02));
  const stroke = $derived(Math.max(2, size * 0.036));
</script>

<svg
  class="rank"
  class:lit
  width={W}
  height={H}
  viewBox="0 0 {W} {H}"
  style:--rank-w="{W}px"
  aria-hidden="true"
  focusable="false"
>
  <text
    x={W / 2}
    y={H * 0.86}
    text-anchor="middle"
    font-size={size}
    stroke-width={stroke}
    paint-order="stroke"
    stroke-linejoin="round"
  >{text}</text>
</svg>

<style>
  .rank {
    display: block;
    flex: none;
    overflow: visible;
  }
  .rank text {
    /*
     * The same face the rest of the app sets, at the heaviest weight it has and
     * set tight — the numeral is a piece of artwork, not a label, and it wants
     * to read as one shape with the poster rather than as type beside it.
     */
    font-family: var(--font);
    font-weight: 800;
    letter-spacing: -0.06em;
    font-variant-numeric: tabular-nums;
    /* The hollow: the ground shows through, the band is the stroke's outer half. */
    fill: var(--bg);
    stroke: color-mix(in oklab, var(--text) 46%, transparent);
    transition: stroke var(--dur) var(--ease);
  }
  .rank.lit text {
    stroke: color-mix(in oklab, var(--text) 88%, transparent);
  }
  @media (prefers-reduced-motion: reduce) {
    .rank text {
      transition: none;
    }
  }
</style>
